import throttle from '../../../utils/throttle';
import { resizeObserver } from '../../utils/dom';
import { supportsMouseInput, supportsTouchInput } from '../utils/support';
import Component from './component';

/**
 * Holds a mapping of slider elements to their attached slider component
 */
const sliders = new Map();

export default class Slider extends Component {
  constructor (props) {
    super(props);

    this.$track = null;
    this.$thumb = null;
    this.$bar = null;

    this._bounds = null;
    this._thumbBounds = null;

    this.onslidestart = this.onslidestart.bind(this);
    this.onslidemove = throttle(this.onslidemove.bind(this), 1000 / 60); // target 60fps
    this.onslideend = this.onslideend.bind(this);
    this.onresize = this.onresize.bind(this); // target 60fps
    this.onkeydown = this.onkeydown.bind(this);
  }

  getDefaultProps () {
    return {
      min: 0,
      max: 100,
      value: 50,
      step: 1,
      keyStep: 1,
      orientation: 'horizontal',
      className: ''
    };
  }

  isSliding () {
    return this.isAttached() && this.$element.hasAttribute('data-sliding');
  }

  attach (element) {
    super.attach(element);

    sliders.set(this.$element, this);

    this.$track = this.$element.querySelector('.iono-slider-track');
    this.$thumb = this.$element.querySelector('.iono-slider-thumb');
    this.$bar = this.$element.querySelector('.iono-slider-bar');

    // this._updateBounds();
    this.$element.setAttribute('role', 'slider');
    this.$element.setAttribute('aria-valuemin', this.props.min);
    this.$element.setAttribute('aria-valuemax', this.props.min);
    this.$element.setAttribute('aria-orientation', this.props.orientation);
    // this._drawValue(this.props.value);

    resizeObserver.observe(this.$element);

    this.$element.addEventListener('resize', this.onresize);
    this.$element.addEventListener('keydown', this.onkeydown);

    if (supportsMouseInput()) {
      this.$element.addEventListener('mousedown', this.onslidestart);
    }

    if (supportsTouchInput()) {
      this.$element.addEventListener('touchstart', this.onslidestart);
    }
  }

  detach () {
    if (this.isAttached()) {
      resizeObserver.unobserve(this.$element);

      this.$element.removeEventListener('resize', this.onresize);
      this.$element.removeEventListener('keydown', this.onkeydown);

      if (supportsMouseInput()) {
        this.$element.removeEventListener('mousedown', this.onslidestart);
        window.removeEventListener('mousemove', this.onslidemove);
        window.removeEventListener('mouseup', this.onslideend);
      }

      if (supportsTouchInput()) {
        this.$element.removeEventListener('touchstart', this.onslidestart);
        this.$element.removeEventListener('touchmove', this.onslidemove);
        this.$element.removeEventListener('touchend', this.onslideend);
        this.$element.removeEventListener('touchcancel', this.onslideend);
      }

      this.$element.removeAttribute('data-sliding');

      this.$track = null;
      this.$thumb = null;
      this.$bar = null;

      sliders.delete(this.$element);
    }

    super.detach();
  }

  render () {
    return `<div class="iono-slider ${this.props.className}" tabindex="0">
      <button class="iono-slider-thumb" tabindex="-1"></button>
      <div class="iono-slider-track">
        <div class="iono-slider-bar"></div>
      </div>
    </div>`;
  }

  update (changes) {
    const shouldRedraw = changes.includes('value') ||
      changes.includes('min') ||
      changes.includes('max') ||
      changes.includes('step');

    if (shouldRedraw) {
      this._drawValue(this.props.value);
    }
  }

  getValue () {
    return this.props.value;
  }

  setValue (value) {
    this.setProp('value', value);
  }

  setProp (prop, value, update = true) {
    if (prop === 'value') {
      value = Math.round(value / this.props.step) * this.props.step;
    }

    return super.setProp(prop, value, update);
  }

  isSliderKey (keyCode) {
    return [37, 39].includes(keyCode);
  }

  // UI Events

  // @todo while keydown step forward or backward
  onkeydown (event) {
    // < 37 - 39 >
    const modifierKeyPressed = (event.altKey || event.shiftKey || event.metaKey || event.ctrlKey);
    if (this.isSliderKey(event.keyCode) && modifierKeyPressed === false) {
      if (event.keyCode === 37) {
        this.setValue(
          Math.max(this.props.min, this.props.value - this.props.keyStep)
        );
      } else if (event.keyCode === 39) {
        this.setValue(
          Math.min(this.props.max, this.props.value + this.props.keyStep)
        );
      }
      this.$element.dispatchEvent(new CustomEvent('input'));
      this.$element.dispatchEvent(new CustomEvent('change'));
    }
  }

  onresize (event) {
    if (event.detail.contentRect.width > 0) {
      this._updateBounds();
    }
  }

  onslidestart (event) {
    this.$element.setAttribute('data-sliding', true);

    this.$element.focus();

    this.props.value = this._getEventValue(event);
    this._drawValue(this.props.value);
    this.$element.dispatchEvent(new CustomEvent('input'));

    if (event instanceof MouseEvent) {
      window.addEventListener('mousemove', this.onslidemove);
      window.addEventListener('mouseup', this.onslideend);
    } else if (event instanceof TouchEvent) {
      this.$element.addEventListener('touchmove', this.onslidemove);
      this.$element.addEventListener('touchend', this.onslideend);
      this.$element.addEventListener('touchcancel', this.onslideend);
    }

    // @todo when thumb loses focus end the slide
    // @todo when user presses ESC revert back to previous value
    // @todo arrow keys step the value
  }

  onslidemove (event) {
    this.props.value = this._getEventValue(event);
    this._drawValue(this.props.value);
    this.$element.dispatchEvent(new CustomEvent('input'));
  }

  onslideend (event) {
    if (event instanceof MouseEvent) {
      window.removeEventListener('mousemove', this.onslidemove);
      window.removeEventListener('mouseup', this.onslideend);
    } else if (supportsTouchInput() && event instanceof TouchEvent) {
      this.$element.removeEventListener('touchmove', this.onslidemove);
      this.$element.removeEventListener('touchend', this.onslideend);
      this.$element.removeEventListener('touchcancel', this.onslideend);
    }

    this.$element.removeAttribute('data-sliding');
    this.$element.dispatchEvent(new CustomEvent('change'));
  }

  _getEventValue (event) {
    const clientX = (supportsTouchInput() && event instanceof TouchEvent) ? event.touches[0].clientX : event.clientX;
    const posX = clientX - this._bounds.left;
    const posP = Math.min(1, Math.max(0, posX / this._bounds.width));

    return Math.round((posP * this.props.max) / this.props.step) * this.props.step;
  }

  _updateBounds (elementBounds = null) {
    this._bounds = elementBounds || this.$element.getBoundingClientRect();
    this._thumbBounds = this.$thumb.getBoundingClientRect();
    this._drawValue(this.props.value);
  }

  _drawValue (value) {
    if (!this.isAttached()) return;

    const posP = value / this.props.max * 100;

    this.$thumb.style.setProperty('left', `${posP}%`);
    this.$bar.style.setProperty('width', `${posP}%`);
    this.$element.setAttribute('aria-valuenow', value);
  }
}

Slider.defaultOptions = {
  min: 0,
  max: 100,
  value: 50,
  step: 1,
  orientation: 'horizontal',
  trackClassName: '.iono-slider-track',
  thumbClassName: '.iono-slider-thumb',
  barClassName: '.iono-slider-bar'
};

Slider.instance = element => {
  return sliders.get(element);
};
